<template>
  <div class="pdf-generator">
    <!-- Bouton pour générer le PDF -->
    <button 
      type="button" 
      @click="generatePDF" 
      class="btn btn-info btn-lg me-3"
      :disabled="isGenerating"
    >
      <span v-if="isGenerating" class="spinner-grow spinner-grow-sm me-2" role="status" aria-hidden="true"></span>
      <i v-if="!isGenerating" class="fas fa-file-pdf me-2"></i>
      {{ isGenerating ? 'Génération...' : 'Aperçu PDF' }}
    </button>

    <!-- Modal pour l'aperçu PDF -->
    <div class="modal fade" id="pdfPreviewModal" tabindex="-1" aria-hidden="true" ref="pdfModal">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-file-pdf me-2"></i>Aperçu du Formulaire de Recensement
            </h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="isGenerating" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Génération du PDF...</span>
              </div>
              <p class="mt-3">Génération de l'aperçu PDF en cours...</p>
            </div>
            
            <div v-else-if="pdfContent" class="pdf-preview">
              <div class="pdf-content" v-html="pdfContent"></div>
            </div>

            <div v-else class="text-center py-5">
              <i class="fas fa-file-pdf fs-1 text-muted"></i>
              <p class="mt-3 text-muted">Cliquez sur "Aperçu PDF" pour générer l'aperçu</p>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              <i class="fas fa-times me-2"></i>Fermer
            </button>
            <button 
              v-if="pdfContent" 
              type="button" 
              @click="printPDF" 
              class="btn btn-primary"
            >
              <i class="fas fa-print me-2"></i>Imprimer
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref,onMounted } from 'vue';

export default defineComponent({
  name: 'PDFGeneratorSimple',
  props: {
    formData: {
      type: Object,
      required: true
    },
    formRef: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const isGenerating = ref(false);
    const pdfContent = ref('');
    const pdfModal = ref();

    onMounted(() => {
      //
    });

    const generatePDF = async () => {
      isGenerating.value = true;
      pdfContent.value = '';

      // Générer le contenu HTML du PDF avec les données actuelles
      const currentFormData = props.formData || {};
      
      try {
        const htmlContent = generateHTMLContent(currentFormData);
        pdfContent.value = htmlContent;

        // Ouvrir le modal
        if (typeof window !== 'undefined' && (window as any).bootstrap) {
          const modal = new (window as any).bootstrap.Modal(pdfModal.value);
          modal.show();
        } else {
          // Fallback si Bootstrap n'est pas disponible
          const modalElement = pdfModal.value;
          if (modalElement) {
            modalElement.classList.add('show');
            modalElement.style.display = 'block';
            modalElement.style.position = 'fixed';
            modalElement.style.top = '0';
            modalElement.style.left = '0';
            modalElement.style.width = '100%';
            modalElement.style.height = '100%';
            modalElement.style.zIndex = '1055';
            document.body.classList.add('modal-open');
          }
        }

      } catch (error) {
        alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
      } finally {
        isGenerating.value = false;
      }
    };

    const generateHTMLContent = (data: any) => {
      
      // Fonction pour générer les checkboxes cochées
      const getCheckboxHTML = (value: string, options: string[]) => {
        return options.map(option => {
          let isChecked = '';
          
          // Pour les tableaux (typeInstallation, besoins)
          if (Array.isArray(data[value])) {
            isChecked = data[value].includes(option.toLowerCase()) ? 'checked' : '';
          }
          // Pour les valeurs simples (sogemaLiquidation, typeMarchand)
          else if (typeof data[value] === 'string') {
            isChecked = data[value].toLowerCase() === option.toLowerCase() ? 'checked' : '';
          }
          
          return `<span class="checkbox-group">
                    <input type="checkbox" ${isChecked}> <label>${option}</label>
                  </span>`;
        }).join('');
      };

      // Fonction pour générer les produits/services
      const getProduitsHTML = () => {
        const produits = data.produits || [];
        let html = '';
        for (let i = 1; i <= 3; i++) {
          const produit = produits[i-1]?.value || '';
          html += `<p>${i}. <span class="long-underline">${produit}</span></p>`;
        }
        return html;
      };

      // Fonction pour les champs conditionnels
      const getAssociationHTML = () => {
        const isOui = data.lienAssociation === 'oui';
        const isNon = data.lienAssociation === 'non';
        return `<span class="checkbox-group">
                  <input type="checkbox" ${isOui ? 'checked' : ''}> <label>Oui (préciser)</label>
                </span>
                <span class="underline">${data.precisionAssociation || ''}</span>
                <span class="checkbox-group">
                  <input type="checkbox" ${isNon ? 'checked' : ''}> <label>Non</label>
                </span>`;
      };

      const getMutualisationHTML = () => {
        const isOui = data.mutualisation === 'oui';
        const isNon = data.mutualisation === 'non';
        return `<span class="checkbox-group">
                  <input type="checkbox" ${isOui ? 'checked' : ''}> <label>Oui (préciser)</label>
                </span>
                <span class="underline">${data.precisionMutualisation || ''}</span>
                <span class="checkbox-group">
                  <input type="checkbox" ${isNon ? 'checked' : ''}> <label>Non</label>
                </span>`;
      };

      return `
        <!DOCTYPE html>
        <html lang="fr">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Formulaire de Recensement des Marchands de Dantokpa</title>
            <style>
                @page {
                    size: A4;
                    margin: 15mm;
                }
                
                body {
                    font-family: Arial, sans-serif;
                    margin: 0;
                    padding: 0;
                    background-color: white;
                    font-size: 12px;
                    line-height: 1.2;
                }
                
                .container {
                    width: 100%;
                    max-width: none;
                    background-color: white;
                    padding: 10px;
                    border: 2px solid #333;
                    box-sizing: border-box;
                    page-break-inside: avoid;
                }
                
                .header {
                    text-align: center;
                    font-weight: bold;
                    font-size: 14px;
                    margin-bottom: 10px;
                    text-transform: uppercase;
                    page-break-after: avoid;
                }
                
                table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-bottom: 8px;
                    font-size: 11px;
                }
                
                td {
                    border: 1px solid #333;
                    padding: 4px;
                    vertical-align: top;
                }
                
                .field-label {
                    font-weight: bold;
                    background-color: #f5f5f5;
                    width: 35%;
                    font-size: 10px;
                }
                
                .field-input {
                    width: 65%;
                }
                
                .checkbox-group {
                    display: inline-block;
                    margin-right: 8px;
                    font-size: 10px;
                }
                
                input[type="checkbox"] {
                    margin-right: 3px;
                    transform: scale(0.8);
                }
                
                .underline {
                    border-bottom: 1px solid #333;
                    display: inline-block;
                    min-width: 120px;
                    margin: 0 3px;
                }
                
                .short-underline {
                    border-bottom: 1px solid #333;
                    display: inline-block;
                    min-width: 60px;
                    margin: 0 3px;
                }
                
                .long-underline {
                    border-bottom: 1px solid #333;
                    display: inline-block;
                    min-width: 200px;
                    margin: 0 3px;
                }
                
                .section-title {
                    font-weight: bold;
                    text-decoration: underline;
                    margin: 8px 0 5px 0;
                    font-size: 11px;
                    page-break-after: avoid;
                }
                
                .numbered-list {
                    margin: 5px 0;
                    font-size: 10px;
                }
                
                .observation-box {
                    width: 100%;
                    height: 40px;
                    border: 1px solid #333;
                    padding: 3px;
                    font-size: 10px;
                }
                
                .compact-section {
                    margin-bottom: 8px;
                    page-break-inside: avoid;
                }
                
                .compact-section p {
                    margin: 3px 0;
                    font-size: 10px;
                }
            </style>
        </head>
        <body>
            <div class="container">
                <div class="header">
                    FORMULAIRE DE RECENSEMENT DES MARCHANDS DE DANTOKPA
                </div>
                
                <div class="section-title">Informations de l'Agent Recenseur</div>
                
                <table>
                    <tr>
                        <td class="field-label">Nom et Prénoms de l'agent recenseur :</td>
                        <td class="field-input"><span class="long-underline">${data.nomPrenomAgent || ''}</span></td>
                    </tr>
                    <tr>
                        <td class="field-label">Téléphone :</td>
                        <td class="field-input"><span class="underline">${data.telephoneAgent || ''}</span></td>
                    </tr>
                </table>
                
                <div class="section-title">Informations du Marchand</div>
                
                <table>
                    <tr>
                        <td class="field-label">Nom et prénom(s) du marchand :</td>
                        <td class="field-input"><span class="long-underline">${data.nomPrenomMarchand || ''}</span></td>
                    </tr>
                    <tr>
                        <td class="field-label">Téléphone :</td>
                        <td class="field-input"><span class="underline">${data.telephoneMarchand || ''}</span></td>
                    </tr>
                    <tr>
                        <td class="field-label">Numéro Personnel d'Identification (NPI) :</td>
                        <td class="field-input"><span class="underline">${data.npi || ''}</span> + copie physique</td>
                    </tr>
                    <tr>
                        <td class="field-label">Secteur d'activité :</td>
                        <td class="field-input"><span class="long-underline">${data.secteurActivite || ''}</span></td>
                    </tr>
                </table>
                
                <div>
                    <p><strong>Produits/Services proposés :</strong></p>
                    <div class="numbered-list">
                        ${getProduitsHTML()}
                    </div>
                </div>
                
                <table>
                    <tr>
                        <td class="field-label">Ancienneté dans l'activité :</td>
                        <td class="field-input"><span class="short-underline">${data.anciennete || ''}</span> années</td>
                    </tr>
                    <tr>
                        <td class="field-label">N° de l'étalage ou boutique :</td>
                        <td class="field-input"><span class="underline">${data.numeroEtalage || ''}</span></td>
                    </tr>
                </table>
                
                <div class="compact-section">
                    <p><strong>SOGEMA-Liquidation :</strong> ${getCheckboxHTML('sogemaLiquidation', ['oui', 'non'])}</p>
                </div>
                
                <div class="compact-section">
                    <p><strong>Type d'installation :</strong> ${getCheckboxHTML('typeInstallation', ['boutique', 'kiosque', 'table', 'ambulant'])}</p>
                </div>
                
                <div class="compact-section">
                    <p><strong>Type de marchand :</strong> ${getCheckboxHTML('typeMarchand', ['locataire', 'exploitant'])}</p>
                </div>
                
                <table>
                    <tr>
                        <td class="field-label">Nombre de personnes travaillant avec vous :</td>
                        <td class="field-input"><span class="underline">${data.nombrePersonnes || ''}</span></td>
                    </tr>
                </table>
                
                <div class="compact-section">
                    <p><strong>Associations/coopératives :</strong> ${getAssociationHTML()}</p>
                </div>
                
                <div class="compact-section">
                    <p><strong>Besoins :</strong> ${getCheckboxHTML('besoins', ['boutique', 'stand'])} <span class="underline">${data.autresBesoin || ''}</span></p>
                </div>
                
                <div class="compact-section">
                    <p><strong>Mutualisation :</strong> ${getMutualisationHTML()}</p>
                </div>
                
                <table>
                    <tr>
                        <td class="field-label">Observations de l'agent recenseur :</td>
                        <td class="field-input">
                            <div class="observation-box">${data.observations || ''}</div>
                        </td>
                    </tr>
                    <tr>
                        <td class="field-label">Date et Signature de l'agent recenseur :</td>
                        <td class="field-input"><span class="long-underline">${data.signatureAgent || ''} - ${data.dateRecensement || ''}</span></td>
                    </tr>
                </table>
            </div>
        </body>
        </html>
      `;
    };

    const closeModal = () => {
      if (typeof window !== 'undefined' && (window as any).bootstrap) {
        const modal = (window as any).bootstrap.Modal.getInstance(pdfModal.value);
        if (modal) {
          modal.hide();
        }
      } else {
        // Fallback pour fermer le modal
        const modalElement = pdfModal.value;
        if (modalElement) {
          modalElement.classList.remove('show');
          modalElement.style.display = 'none';
          document.body.classList.remove('modal-open');
        }
      }
    };

    const printPDF = () => {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Formulaire de Recensement - Dantokpa</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
                @media print {
                  body { margin: 0; padding: 0; }
                  .no-print { display: none; }
                }
              </style>
            </head>
            <body>
              ${pdfContent.value}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    };

    return {
      isGenerating,
      pdfContent,
      pdfModal,
      generatePDF,
      printPDF,
      closeModal
    };
  }
});
</script>

<style scoped>
.pdf-generator {
  display: inline-block;
}

.pdf-preview {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  overflow: hidden;
  max-height: 70vh;
  overflow-y: auto;
}

.modal-xl {
  max-width: 90vw;
}

@media (max-width: 768px) {
  .modal-xl {
    max-width: 95vw;
  }
}

.pdf-content {
  background: white;
  color: black;
}

@media print {
  .pdf-content {
    box-shadow: none;
    border: none;
  }
}
</style>
